import imageCompression from "browser-image-compression";
import currencyToSymbolMap from "currency-symbol-map/map";
import { DateTime } from "luxon";
import moment from "moment";
import toast from "react-hot-toast";

import { CurrentUser } from "@/types";

import { isPacPlatform, isQuebecPlatform, isUspaPlatform } from "./uspa-utils/uspa-platform-utils";

export const parseToJSON = (value) => {
  let parsedValue: any[] | null = null;
  try {
    parsedValue = value ? JSON.parse(value) : null;
  } catch (error) {
    console.warn("Failed to parse parseVaue:", error);
    parsedValue = [];
  }
  return parsedValue;
};

export const getDateDifference = (fromDate, toDate) => {
  const start = moment(fromDate);
  const end = moment(toDate);

  if (start.isAfter(end)) {
    throw new Error("The 'fromDate' must be earlier than the 'toDate'");
  }

  // Calculate differences
  const years = end.diff(start, "years");
  start.add(years, "years");

  const months = end.diff(start, "months");
  start.add(months, "months");

  const days = end.diff(start, "days");

  // Build result string
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} month${months > 1 ? "s" : ""}`);
  if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);

  return parts.length > 0 ? parts.join(", ") : "0 days";
};

export const getTournamentStatus = (
  entryDeadlineDate,
  tournamentStartDate,
  tournamentEndDate,
  tournamentStatus,
  tournamentApprovalStatus
) => {
  const currentDate = new Date();

  const status = (() => {
    // if there is approval status is pending in the case return Waiting for confirmation
    if (tournamentApprovalStatus === "pending") return "Waiting confirmation";
    // if there is approval status is rejected in the case return rejected
    if (tournamentApprovalStatus === "declined") return "Declined";
    if (tournamentApprovalStatus === "postponed") return "Postponed";
    if (tournamentApprovalStatus === "deleted") return "Deleted";
    if (tournamentStatus === "inactive") return "inactive";
    // if there is tournament status is inctive then return inactive
    // if (currentDate <= new Date(entryDeadlineDate)) return "registration";
    if (new Date(tournamentStartDate) >= currentDate) return "Upcoming";

    // if (currentDate >= entryDeadlineDate && currentDate <= tournamentStartDate) return 'deadline past';
    if (currentDate >= new Date(tournamentStartDate) && currentDate <= new Date(tournamentEndDate))
      return "in progress";
    if (currentDate >= new Date(tournamentEndDate)) return "completed";
    return undefined;
  })();

  const getStepTitle = (): string => {
    switch (status) {
      // case "registration":
      //   return "Upcoming";
      case "Upcoming":
        return "Upcoming";
      case "in progress":
        return "In progress";
      case "completed":
        return "Completed";
      case "inactive":
        return "Inactive";
      case "Waiting confirmation":
        return "Waiting confirmation";
      case "Declined":
        return "Declined";
      case "Postponed":
        return "Postponed";
      case "Deleted":
        return "Deleted";
      default:
        return "Confirmation";
    }
  };

  return getStepTitle();
};

export const Compression = async (file: File, maxSizeMB = 1.5) => {
  const initialQuality = 1.1;

  try {
    const { width, height } = await getImageDimensions(file);

    // Maintain aspect ratio by choosing the larger dimension
    const maxWidthOrHeight = Math.max(width, height) > 1200 ? 1200 : Math.max(width, height);

    const options = {
      maxSizeMB,
      maxWidthOrHeight, // Dynamically set based on image dimensions
      useWebWorker: true,
      initialQuality,
      alwaysKeepResolution: true,
    };

    const compressedFile = await toast.promise(imageCompression(file, options), {
      loading: "Compressing...",
      success: "Successfully compressed.",
      error: "Error compressing the file.",
    });

    return compressedFile;
  } catch (error) {
    console.error("Error during compression:", error);
    throw error;
  }
};

export function prettifyDates(
  startDate: string | moment.Moment,
  endDate: string | moment.Moment,
  timeZone?: string | moment.Moment
) {
  const start = typeof startDate === "string" ? moment(startDate) : startDate;
  const end = typeof endDate === "string" ? moment(endDate) : endDate;

  if (start.isSame(end, "day")) {
    return (
      start.format(`MMM D, YYYY${timeZone ? ` HH:mm - ${end.format("HH:mm")}` : ""}`) +
      (timeZone ? ` ${timeZone}` : "")
    );
  } else if (start.year() !== end.year()) {
    return `${start.format(
      `MMM D, YYYY${timeZone ? ` HH:mm` : ""}`
    )} - ${end.format(`MMM D, YYYY${timeZone ? ` HH:mm` : ""}`)} ${timeZone ?? ""}`;
  } else if (start.month() !== end.month()) {
    return `${start.format(`MMM D${timeZone ? ` HH:mm` : ""}`)} - ${end.format(
      `MMM D, YYYY${timeZone ? ` HH:mm` : ""}`
    )} ${timeZone ?? ""}`;
  } else {
    return `${start.format(`MMM D${timeZone ? ` HH:mm` : ""}`)} - ${end.format(
      `D, YYYY${timeZone ? ` HH:mm` : ""}`
    )} ${timeZone ?? ""}`;
  }
}

export const handleSetImage = (file, setFunction) => {
  Compression(file[0], 1)
    .then((compressedBlob) => {
      const compressedFile = new File([compressedBlob], file[0].name, {
        type: compressedBlob.type,
      });

      setFunction([compressedFile]);
    })
    .catch((error) => {
      console.error("Error while compressing the file:", error);
    });
};
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(url); // Clean up
    };

    img.onerror = () => {
      reject(new Error("Failed to load image."));
    };

    img.src = url;
  });
};

export const isPlatformManager = (currentPlatform: { id: string }, currentUser: CurrentUser) => {
  //allow the access if the login user is the member of platform
  return currentUser.memberOfPlatformIds.includes(currentPlatform.id);
};

/**
 * @deprecated Identity check via mainManager email. The audience-split REST surface
 * deliberately omits email from `DashboardUserRef`, so this can no longer compare by
 * email — it compares by id instead, which is what callers actually want. Switch
 * call sites to `isFederationOwner(currentUser, currentPlatform.owner)` and remove.
 */
export const isFederationEmail = (
  currentPlatform: { owner?: { mainManager?: { id: number } | null } | null },
  currentUser: { id: number }
) => {
  return (
    currentPlatform?.owner?.mainManager?.id != null &&
    currentPlatform.owner.mainManager.id === currentUser.id
  );
};

const currencyList = Object.entries({ ...currencyToSymbolMap }).map(([code, symbol]) => ({
  code,
  label: code,
  symbol,
}));

export default currencyList;

export const convertDateToNewTimezone = (dateStr, newTimezone, isUTC = false) => {
  if (!dateStr || !newTimezone) {
    // console.error('Missing required values:', { dateStr, newTimezone });
    return dateStr;
  }
  try {
    // Convert the date to a DateTime object in the selected timezone
    const localTime = DateTime.fromISO(dateStr, { zone: newTimezone });
    if (!localTime.isValid) {
      console.error("Invalid DateTime:", localTime.invalidReason);
      return dateStr;
    }
    // Convert to the new timezone while maintaining local time
    return isUTC ? localTime.toUTC().toISO() : localTime.toISO();
  } catch (error) {
    console.error("Error adjusting timezone:", error);
    return dateStr;
  }
};

export const replaceLeagueName = (name: string, platformPublicId: string | null | undefined) => {
  const useCircuitterm =
    isUspaPlatform(platformPublicId) ||
    isPacPlatform(platformPublicId) ||
    isQuebecPlatform(platformPublicId);

  let title;
  if (useCircuitterm) {
    if (name.toLowerCase() === "league") {
      title = "Circuit";
    }
    if (name.toLowerCase() === "leagues") {
      title = "Circuits";
    }
  }
  return title || name;
};

export const handleRoleColor = (role) => {
  switch (role) {
    case "coach":
      return {
        color: "#DD790D",
        background: "#DD790D1A",
      };
    case "clubAdministrator":
      return {
        color: "#2C8A3F",
        background: "#2C8A3F1A",
      };

    case "fan":
      return {
        color: "#1865AD",
        background: "#1865AD1A",
      };
    case "federation":
      return {
        color: "#364FC6",
        background: "#364FC61A",
      };
    case "superAdmin":
      return {
        color: "#5D3CC3",
        background: "#5D3CC31A",
      };
    case "athlete":
      return {
        color: "#862E9C",
        background: "#862E9C1A",
      };
    case "user":
      return {
        color: "#A61E4E",
        background: "#A61E4E1A",
      };
    case "Other":
      return {
        color: "#2ACA9F",
        background: "#2ACA9F1A",
      };
    default:
      return { color: "#333", background: "#eee" };
  }
};

export function getThirdpartyName(id) {
  switch (Number(id)) {
    case 1:
      return "ipinId";
    case 4:
      return "duprId";

    case 6:
      return "ltsU10Id";
    case 2:
      return "atpId";
    case 3:
      return "wtaId";
    case 5:
      return "fideId";
    case 8:
      return "teU12Id";
    case 9:
      return "otherPoints";
    case 13:
      return "bonusPoints";
    case 12:
      return "bonusU16";
    case 11:
      return "bonusU14";
    case 10:
      return "bonusU12";
    case 14:
      return "wprUserId";
    case 15:
      return "itf_points";
    case 16:
      return "fivb";
    case 18:
      return "wprUserId";

    default:
      return "";
  }
}

// Add this utility function at the top of the component
export const hasNonLatinCharacters = (text: string): boolean => {
  if (!text) return false;
  // Allows visible ASCII, common whitespace, and Latin blocks; flags anything else
  const nonLatinRegex =
    /[^\u0020-\u007E\u00A0-\u00FF\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]/u;
  return nonLatinRegex.test(text);
};
export const isNullish = (value) => {
  return value === null || value === undefined;
};

export const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export function getCurrencySymbol(currencyName: any): string {
  return (
    (currencyList.find((item) => item?.label === currencyName)?.symbol as string) ||
    "Symbol not found"
  );
}
export const handleLeagueManagerRoleColor = (role) => {
  switch (role) {
    case "referee":
      return "primary";
    case "organizer":
      return "danger";
    case "manager":
      return "success";

    default:
      return "success";
  }
};

export const handleSignupSource = (source) => {
  switch (source) {
    case "WEB":
      return "Web App";
    case "MOBILE":
      return "Mobile App";

    default:
      return "Web App";
  }
};

export const sanitizeFileName = (fileName: string): string => {
  return fileName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_")
    .replace(/\./g, "_")
    .replace(/,/g, "_")
    .replace(/[/\\:*?"<>|]/g, "")
    .replace(/[^a-zA-Z0-9_.-]/g, "");
};

export const downloadFile = async (fileUrl, fileName = ""): Promise<void> => {
  let name = fileName
    ? fileName
    : fileUrl?.split("-")[fileUrl?.split("-")?.length - 1] || "download";

  // Sanitize the filename to avoid special characters
  if (fileName) {
    name = fileName;
  }

  // Sanitize the file name
  name = sanitizeFileName(name);

  const image = await fetch(fileUrl);
  const imageBlog = await image.blob();
  const imageURL = URL.createObjectURL(imageBlog);
  const link = document.createElement("a");
  link.href = imageURL;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatTimeWithTimezone = (
  date: string | Date | moment.Moment | null | undefined
): string => {
  if (!date) return "";

  const time = typeof date === "string" || date instanceof Date ? moment(date) : date;

  if (!time.isValid()) return "";

  const offset = time.utcOffset() / 60;
  const offsetStr = offset >= 0 ? `+${offset}` : `${offset}`;
  return `${time.format("HH:mm:ss")} UTC${offsetStr}`;
};

export const dateFormate = (birthDate) => {
  const dateStr =
    typeof birthDate === "string"
      ? birthDate
      : birthDate instanceof Date
        ? birthDate.toISOString()
        : String(birthDate);
  // Check if date is in ISO format with timezone
  const isIsoFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(dateStr);

  if (isIsoFormat) {
    // Parse ISO format without UTC conversion
    return moment(dateStr).format("DD.MM.YYYY");
  } else {
    // Parse other formats with UTC conversion
    return moment.utc(dateStr, ["DD/MM/YYYY", "DD.MM.YYYY", "YYYY-MM-DD"]).format("DD.MM.YYYY");
  }
};

export const calculateEstimatedFee = (
  percentage: number,
  rangeStart: number,
  rangeEnd?: number
) => {
  const min = ((percentage * rangeStart) / 100).toFixed(2);

  const max = rangeEnd ? ((percentage * rangeEnd) / 100).toFixed(2) : "∞";

  return { min, max };
};
